import {
  IAccountRateCreateReqSchema,
  IAccountRateUpdateReqSchema,
  IPriceUnderProductSchemaUI,
  IRateReqSchema,
  IRateResBaseSchema,
  IRateResSchema,
} from '@monetize/types/app';
import { QueryClient, useQueries, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { handleApiErrorToast } from '../api/axios';
import {
  doCreateOfferingRate,
  doDeleteRate,
  doGetOfferingRate,
  doUpdateOfferingRate,
  productCatalogServiceQueryKeys,
} from '../api/productCatalogService';
import { getOrderedPricesUnderProduct } from '../utils/product';

export const cacheOfferingRate = (
  queryClient: QueryClient,
  rate: IRateResSchema,
) => {
  queryClient.setQueryData(
    productCatalogServiceQueryKeys.ratesById(rate.id),
    rate,
  );
};

/**
 * A rate the query cache already holds, without fetching. Used to seed the
 * hook's state on mount so a consumer whose rate was prefetched renders with it
 * on the first pass instead of one effect later.
 */
const getCachedOfferingRate = (
  queryClient: QueryClient,
  rateId?: string,
): IRateResSchema | null =>
  (rateId &&
    queryClient.getQueryData<IRateResSchema>(
      productCatalogServiceQueryKeys.ratesById(rateId),
    )) ||
  null;

/**
 * @param id Offering that createOfferingRate and updateOfferingRate act on when
 *   they are called without an explicit offering id
 * @param initialRateId Rate to hydrate from cache on mount, if it is already there
 */
const useOfferingRate = (id?: string, initialRateId?: string) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [removeLoading, setRemoveLoading] = useState<boolean>(false);

  /**
   * Read from cache once, on mount. Every state below seeds from this rather
   * than from each other, so the declarations can be reordered safely.
   */
  const [initialRate] = useState(() =>
    getCachedOfferingRate(queryClient, initialRateId),
  );

  const [offeringRate, setOfferingRate] = useState<IRateResSchema | null>(
    initialRate,
  );
  const [orderedPricesUnderProduct, setOrderedPricesUnderProduct] = useState<
    IPriceUnderProductSchemaUI[]
  >(() => (initialRate ? getOrderedPricesUnderProduct(initialRate) : []));
  const [offeringRatesMap, setOfferingRatesMap] = useState<
    Record<string, IRateResBaseSchema>
  >(() => (initialRate ? { [initialRate.id]: initialRate } : {}));

  // Tracking the hydrated rate keeps it subscribed to later cache updates,
  // exactly as if fetchOfferingRate had loaded it
  const [trackedRateIds, setTrackedRateIds] = useState<string[]>(() =>
    initialRate ? [initialRate.id] : [],
  );

  const trackRateId = useCallback((rateId: string) => {
    setTrackedRateIds((prev) =>
      prev.includes(rateId) ? prev : [...prev, rateId],
    );
  }, []);

  const sharedRates = useQueries({
    queries: trackedRateIds.map((rateId) => ({
      queryKey: productCatalogServiceQueryKeys.ratesById(rateId),
      queryFn: () => doGetOfferingRate(rateId),
      enabled: false,
    })),
    combine: (results) =>
      results
        .map(({ data }) => data)
        .filter((rate): rate is IRateResSchema => !!rate),
  });

  useEffect(() => {
    if (!sharedRates.length) {
      return;
    }
    setOfferingRatesMap((prev) => {
      const staleRates = sharedRates.filter((rate) => prev[rate.id] !== rate);
      if (!staleRates.length) {
        return prev;
      }
      const next = { ...prev };
      staleRates.forEach((rate) => {
        next[rate.id] = rate;
      });
      return next;
    });
  }, [sharedRates]);

  const currentSharedRate = offeringRate
    ? sharedRates.find((rate) => rate.id === offeringRate.id)
    : undefined;

  useEffect(() => {
    if (!currentSharedRate || currentSharedRate === offeringRate) {
      return;
    }
    setOfferingRate(currentSharedRate);
    setOrderedPricesUnderProduct(
      getOrderedPricesUnderProduct(currentSharedRate),
    );
  }, [currentSharedRate, offeringRate]);

  const fetchOfferingRate = useCallback(
    async (
      rateId: string,
      forceRefresh = false,
    ): Promise<IRateResSchema | null> => {
      trackRateId(rateId);
      setIsFetching(true);
      try {
        /**
         * One store, one request. fetchQuery returns the cached rate, joins a
         * request already in flight for the same key, or issues one, so this
         * hook and the table's rate prefetch can never duplicate a GET. A
         * forced refresh marks the entry stale so it refetches instead.
         */
        const res = await queryClient.fetchQuery({
          queryKey: productCatalogServiceQueryKeys.ratesById(rateId),
          queryFn: () => doGetOfferingRate(rateId),
          staleTime: forceRefresh ? 0 : Infinity,
          retry: false,
        });
        setOrderedPricesUnderProduct(getOrderedPricesUnderProduct(res));
        setOfferingRate(res);
        setOfferingRatesMap((prev) => ({
          ...prev,
          [res.id]: res,
        }));
        return res;
      } catch (error) {
        handleApiErrorToast(error);
        return null;
      } finally {
        setIsFetching(false);
      }
    },
    [queryClient, trackRateId],
  );

  const createOfferingRate = async (
    data: IRateReqSchema | IAccountRateCreateReqSchema,
    offeringId: string | undefined = id,
  ): Promise<IRateResSchema | null> => {
    if (!offeringId) {
      throw new Error('offering id is required to create rate');
    }

    setLoading(true);

    try {
      const res = await doCreateOfferingRate(
        {
          ...data,
          id: undefined, // id and customId are set with a fake UUID
          customId: undefined, // id and customId are set with a fake UUID
        },
        offeringId,
      );

      cacheOfferingRate(queryClient, res);
      trackRateId(res.id);
      setOfferingRate(res);
      setOfferingRatesMap((prev) => ({
        ...prev,
        [res.id]: res,
      }));

      queryClient.invalidateQueries({
        queryKey: productCatalogServiceQueryKeys.orderables(),
      });

      queryClient.invalidateQueries({
        queryKey: productCatalogServiceQueryKeys.offeringsById(offeringId),
      });

      return res;
    } catch (error) {
      handleApiErrorToast(error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateOfferingRate = async (
    data: IRateReqSchema | IAccountRateUpdateReqSchema,
    rateId: string,
    offeringId: string | undefined = id,
  ): Promise<IRateResSchema | null> => {
    if (!offeringId) {
      throw new Error('offering id is required to update rate');
    }

    setLoading(true);

    try {
      const res = await doUpdateOfferingRate(data, offeringId, rateId);

      setOfferingRate(res);
      setOfferingRatesMap((prev) => ({
        ...prev,
        [res.id]: res,
      }));
      cacheOfferingRate(queryClient, res);
      trackRateId(res.id);

      queryClient.invalidateQueries({
        queryKey: productCatalogServiceQueryKeys.orderables(),
      });

      queryClient.invalidateQueries({
        queryKey: productCatalogServiceQueryKeys.offeringsById(offeringId),
      });

      return res;
    } catch (error) {
      handleApiErrorToast(error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const removeOfferingRate = async (rateId: string): Promise<boolean> => {
    setRemoveLoading(true);
    try {
      const res = await doDeleteRate(rateId);
      queryClient.removeQueries({
        queryKey: productCatalogServiceQueryKeys.ratesById(res.id),
      });

      setTrackedRateIds((prev) =>
        prev.filter((trackedId) => trackedId !== res.id),
      );
      setOfferingRate(null);
      setOfferingRatesMap((prev) => {
        const newMap = { ...prev };
        delete newMap[res.id];
        return newMap;
      });
      return true;
    } catch (error) {
      handleApiErrorToast(error);
      return false;
    } finally {
      setRemoveLoading(false);
    }
  };

  return {
    isFetching,
    loading,
    removeLoading,
    offeringRate,
    orderedPricesUnderProduct,
    offeringRatesMap,
    setOrderedPricesUnderProduct,
    setOfferingRate,
    fetchOfferingRate,
    createOfferingRate,
    updateOfferingRate,
    removeOfferingRate,
  };
};

export default useOfferingRate;
